import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getToken } from '../../services/auth';

interface Template {
  id: number;
  name: string;
  body_html: string;
  procedure_type?: string;
}

interface Props {
  patientId: string;
  visitId?: number;
  onClose: () => void;
}

export default function ConsentForm({ patientId, visitId, onClose }: Props) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [witnessName, setWitnessName] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    axios.get('/api/consent/templates', { headers })
      .then((r) => setTemplates(r.data || []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startDraw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const endDraw = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) setSignatureData(canvas.toDataURL('image/png'));
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData('');
  };

  const save = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      await axios.post('/api/consent/sign', {
        patient_id: patientId,
        template_id: selectedTemplate.id,
        visit_id: visitId,
        consent_text: selectedTemplate.body_html,
        signature_data: signatureData,
        witness_name: witnessName,
      }, { headers });
      setSaved(true);
      setTimeout(() => { setSaved(false); onClose(); }, 1500);
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 24, width: 600, maxHeight: '90vh',
        overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', color: '#1e293b' }}>📝 Consent Form</h3>

        {/* Template Selection */}
        {!selectedTemplate ? (
          <div>
            <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 12px' }}>Select a consent form template:</p>
            {templates.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>
                No templates found. Create templates in Admin → Consent Templates.
              </div>
            )}
            {templates.map((t) => (
              <div key={t.id} onClick={() => setSelectedTemplate(t)}
                style={{
                  padding: 12, borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 8,
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#8b5cf6')}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')}>
                <div style={{ fontWeight: 600, color: '#1e293b' }}>{t.name}</div>
                {t.procedure_type && <div style={{ fontSize: 11, color: '#64748b' }}>For: {t.procedure_type}</div>}
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        ) : (
          <div>
            <button onClick={() => setSelectedTemplate(null)} style={{
              padding: '4px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff',
              cursor: 'pointer', fontSize: 12, marginBottom: 12,
            }}>← Back to templates</button>

            <h4 style={{ margin: '0 0 8px', color: '#334155' }}>{selectedTemplate.name}</h4>
            <div style={{
              padding: 16, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0',
              marginBottom: 16, fontSize: 13, lineHeight: 1.6, maxHeight: 200, overflowY: 'auto',
            }} dangerouslySetInnerHTML={{ __html: selectedTemplate.body_html }} />

            {/* Signature Pad */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>Patient Signature</label>
                <button onClick={clearSignature} style={{
                  padding: '2px 8px', borderRadius: 4, border: '1px solid #e2e8f0', background: '#fff',
                  cursor: 'pointer', fontSize: 11, color: '#94a3b8',
                }}>Clear</button>
              </div>
              <canvas ref={canvasRef} width={540} height={100}
                onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                style={{
                  border: '2px dashed #e2e8f0', borderRadius: 8, cursor: 'crosshair',
                  background: '#fafafa', width: '100%', height: 100,
                }} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, fontSize: 13, color: '#334155', display: 'block', marginBottom: 4 }}>Witness Name</label>
              <input value={witnessName} onChange={(e) => setWitnessName(e.target.value)}
                placeholder="Optional" style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0', boxSizing: 'border-box' }} />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving || !signatureData} style={{
                padding: '8px 20px', borderRadius: 8, border: 'none', fontWeight: 600, cursor: 'pointer',
                background: saved ? '#059669' : signatureData ? 'linear-gradient(135deg, #8b5cf6, #3b82f6)' : '#94a3b8',
                color: '#fff', opacity: signatureData ? 1 : 0.5,
              }}>{saving ? 'Saving...' : saved ? '✓ Consent Signed' : 'Sign & Submit'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
